import React, { useEffect, useMemo } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { Lock, X } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface AuthNudgePosition {
  x: number;
  y: number;
}

interface AuthNudgeProps {
  isOpen: boolean;
  message: string;
  actionLabel: string;
  position: AuthNudgePosition;
  onAction: () => void;
  onClose: () => void;
  autoCloseMs?: number;
}

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

export const AuthNudge: React.FC<AuthNudgeProps> = ({
  isOpen,
  message,
  actionLabel,
  position,
  onAction,
  onClose,
  autoCloseMs = 4500
}) => {
  useEffect(() => {
    if (!isOpen) return;
    const t = window.setTimeout(() => onClose(), autoCloseMs);
    return () => window.clearTimeout(t);
  }, [autoCloseMs, isOpen, onClose]);

  const style = useMemo(() => {
    const padding = 12;
    const width = 260;
    const height = 92;

    const x = clamp(
      position.x + 12,
      padding,
      window.innerWidth - width - padding
    );
    const y = clamp(
      position.y + 12,
      padding,
      window.innerHeight - height - padding
    );

    return {
      left: x,
      top: y,
      width
    } as const;
  }, [position.x, position.y]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: 6, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 6, scale: 0.98 }}
          transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }}
          style={style}
          className={cn(
            'fixed z-90 rounded-2xl border border-emerald-100 bg-white/95 shadow-xl',
            'backdrop-blur-sm supports-backdrop-filter:bg-white/80'
          )}
          role="status"
          aria-live="polite"
        >
          <div className="flex items-start gap-3 p-3">
            <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100">
              <Lock className="h-4 w-4" />
            </div>

            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-gray-900">Sign in</p>
              <p className="mt-0.5 text-xs leading-snug text-gray-600">
                {message}
              </p>

              <div className="mt-2 flex items-center gap-2">
                <button
                  type="button"
                  onClick={onAction}
                  className="inline-flex h-8 items-center justify-center rounded-lg bg-gray-900 px-3 text-xs font-semibold text-white transition-colors hover:bg-gray-800 focus:outline-hidden focus:ring-2 focus:ring-gray-900/20"
                >
                  {actionLabel}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex h-8 items-center justify-center rounded-lg px-2 text-xs font-semibold text-gray-600 transition-colors hover:bg-gray-100 focus:outline-hidden focus:ring-2 focus:ring-gray-900/10"
                >
                  Not now
                </button>
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AuthNudge;
